"use client";

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { isThetaError } from "@theta/api-client";
import { errorText } from "@/lib/errors";
import { getReportTargets, type GroupSummary } from "@/lib/api/messages";
import { useReportSender } from "@/lib/hooks/useMessageRequests";
import { retryUnlessNotFound } from "@/lib/hooks/useConversation";
import { personName } from "./participants";
import { SharedGroupChip } from "./SharedGroupChip";
import { Dialog } from "@/components/ui/Dialog";

/**
 * "Anmeld for spam" — reachable from a message request and from an open
 * conversation.
 *
 * The report goes to the admins of ONE group the two of you share, chosen here.
 * The server re-validates that choice against the current memberships, so this
 * picker is a convenience, never the authorisation.
 *
 * `can.report` is that authorisation, and it carries ALL THREE of the route's
 * refusals — a shared group, no duplicate, and the evidence rule — so the
 * submit button it draws is never a button on a request that always 422s.
 */
export function ReportDialog({
  conversationId,
  userId,
  defaultBlock,
  onClose,
  onReported,
}: {
  conversationId: number;
  /** Absent defaults, server-side, to the conversation's CREATOR. */
  userId?: number;
  /** Pre-tick "block and hide" when opened from a request, not from a thread. */
  defaultBlock: boolean;
  onClose: () => void;
  onReported?: (result: { blocked: boolean; declined: boolean }) => void;
}) {
  const [groupId, setGroupId] = useState<GroupSummary["id"] | null>(null);
  const [reason, setReason] = useState("");
  const [block, setBlock] = useState(defaultBlock);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, isError, error: targetsError } = useQuery({
    queryKey: ["v1", "report-targets", conversationId, userId ?? null],
    queryFn: () => getReportTargets(conversationId, userId),
    staleTime: 60 * 1000,
    // A 404 here is the D7 answer — the other account is gone, or the last
    // shared group was left — and it will never become anything else.
    // Retrying it is retrying a correct answer, and then telling the user to
    // "Prøv igen" against it is an instruction that cannot succeed.
    retry: retryUnlessNotFound,
  });

  const report = useReportSender(conversationId);

  const target = data?.user;
  const groups = data?.groups ?? [];
  const targetName = target ? personName(target) : "personen";
  const alreadyReported = data?.alreadyReported === true;

  // Auto-select when there is nothing to choose between. Derived rather than
  // pushed into state by an effect, so the picker has a valid value on the very
  // first render after the targets load.
  const selectedGroupId =
    groupId ?? (groups.length === 1 ? groups[0].id : null);

  async function handleSubmit() {
    if (!target || selectedGroupId === null) return;
    setError(null);
    try {
      const result = await report.mutateAsync({
        userId: target.id,
        groupId: selectedGroupId,
        reason: reason.trim() || undefined,
        block,
      });
      onReported?.({ blocked: result.blocked, declined: result.declined });
      onClose();
    } catch (e) {
      // One error shape: the server's own Danish message when it has one —
      // its per-field ones first, where it sent them (OSO-443).
      setError(errorText(e, "Anmeldelsen kunne ikke sendes. Prøv igen."));
    }
  }

  return (
    <Dialog open onClose={onClose} align="top" label="Anmeld for spam" className="p-5">
      <h2 className="text-sm font-semibold text-foreground mb-3">
        Anmeld for spam
      </h2>

      {isLoading && (
        <p className="text-sm text-subtle-foreground py-4">
          Henter…
        </p>
      )}

      {isError && !isLoading && (
        <p
          className={`text-sm py-4 ${
            isThetaError(targetsError, "notFound")
              ? "text-subtle-foreground"
              : "text-destructive"
          }`}
        >
          {isThetaError(targetsError, "notFound")
            ? "Denne samtale kan ikke anmeldes — I deler ikke længere en gruppe."
            : "Anmeldelsen kunne ikke forberedes. Prøv igen."}
        </p>
      )}

      {alreadyReported && (
        <p className="text-sm text-foreground py-4">
          Anmeldt ✓ — du har allerede anmeldt {targetName} i denne samtale.
        </p>
      )}

      {data && !alreadyReported && (
        <div className="space-y-4">
          <p className="text-sm text-foreground">
            Du er ved at anmelde{" "}
            <strong className="font-semibold text-foreground">
              {targetName}
            </strong>
            .
          </p>

          <div>
            <label
              htmlFor="dm-report-group"
              className="block text-sm font-medium text-foreground mb-1"
            >
              Send anmeldelsen til administratorerne i
            </label>
            {groups.length === 0 ? (
              <p className="text-xs text-subtle-foreground">
                Du deler ingen grupper med denne person
              </p>
            ) : (
              <>
                <select
                  id="dm-report-group"
                  value={selectedGroupId ?? ""}
                  onChange={(e) =>
                    setGroupId(
                      groups.find((g) => String(g.id) === e.target.value)?.id ??
                        null
                    )
                  }
                  className="w-full px-4 py-2.5 text-sm bg-surface border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Vælg en gruppe</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  {groups.map((group) => (
                    <SharedGroupChip key={group.id} group={group} />
                  ))}
                </div>
              </>
            )}
          </div>

          <div>
            <label
              htmlFor="dm-report-reason"
              className="block text-sm font-medium text-foreground mb-1"
            >
              Begrundelse
            </label>
            <textarea
              id="dm-report-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Valgfri begrundelse..."
              className="w-full px-4 py-2.5 text-sm bg-surface border border-border rounded-xl text-foreground placeholder:text-subtle-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <label className="flex items-start gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={block}
              onChange={(e) => setBlock(e.target.checked)}
              className="mt-0.5 rounded border-border-strong text-indigo-600 focus:ring-indigo-500"
            />
            Blokér afsenderen og skjul samtalen
          </label>

          <p className="text-xs text-subtle-foreground">
            Gruppens administratorer ser kun de beskeder, {targetName} har
            sendt dig — resten af samtalen er privat.
          </p>

          {error && (
            <p className="text-xs text-destructive">{error}</p>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-foreground bg-muted rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          {alreadyReported ? "Luk" : "Annuller"}
        </button>
        {!alreadyReported && (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={
              !data ||
              !data.can.report ||
              groups.length === 0 ||
              selectedGroupId === null ||
              report.isPending
            }
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50"
          >
            {report.isPending ? "Sender..." : "Send anmeldelse"}
          </button>
        )}
      </div>
    </Dialog>
  );
}
